// cSpell:ignore afrekeningsbedrijf Bedrag décompte eigenaar Elel Gelijke laste Montant pand pand's panden sleutel société verbruik verdeelsleutel verdeling Werkelijk
/* eslint-disable no-nested-ternary */
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Icon,
  Select,
  TextStyle,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React, { useState } from "react";
import styled from "styled-components";
import { KeyCoverageWarning } from "../../../../components/BuildingDistribution";
import { MoneyWithVat } from "../../../../components/Money";
import { departureKeyDifferences } from "../../../../departure.utils";
import {
  ComputeResult,
  isPerOccupant,
  keyCoverage,
  occupantKey,
} from "../../../../engine";
import { existedSince, keySplitsForCosts } from "../../../../groupUnits.utils";
import { keyDisplayName } from "../../../../keyName.utils";
import { addsUpTo, fromCents, toCents } from "../../../../money.utils";
import {
  ApportionmentKey,
  Category,
  CATEGORY_LABELS_NL,
  departureMethodsForCategory,
  DistributionMethod,
  Invoice,
  ISettledDeparture,
  methodLabel,
  methodsForCategory,
  resolveDistributionMethod,
  settlementEligible,
  settlementAmount,
} from "../../../../types";
import { ContractOverlapWarning } from "./ContractOverlapWarning";
import {
  DistributionEditorModal,
  tenantsWithoutConsumption,
} from "./DistributionEditorModal";
import {
  btwOf,
  cardTitleStyle,
  categoryIcon,
  fmt,
  fmtDate,
  StepHeader,
} from "./shared";
import { StepContext } from "./types";

// A category's row (Lewis, 2026-10-17: with "Werkelijk verbruik (kWh)"
// chosen the name was squeezed to "Elel (1)" behind the select): the icon
// and the name with its count, never cut (it wraps between its words), then
// the method at a fixed width, the edit button or the key, and the chevron
// on the right. When the card is too narrow for all of it, the controls go
// onto a line of their own under the name, still on the right; anything
// else about the category goes on a line under the row (CardNotes).
const METHOD_SELECT_WIDTH = 260;
// The room the name keeps before the controls move under it.
const TITLE_MIN_WIDTH = 220;

const CardHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 ${TITLE_MIN_WIDTH}px;
  min-width: 0;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.getColor("primary", 0)};
`;

// The card's title as the other steps' cards write theirs.
const HeadTitle = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: normal;
  word-break: normal;
  ${cardTitleStyle}
`;

const HeadControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex: 0 1 auto;
  margin-left: auto;
`;

// The method's and the key's select, at one width whatever their label
// ("Bedrag (afrekeningsbedrijf)", "Montant (société de décompte)").
const SelectControl = styled.div`
  flex: 0 0 ${METHOD_SELECT_WIDTH}px;
  width: ${METHOD_SELECT_WIDTH}px;
  max-width: 100%;
`;

// Only "Ten laste van de eigenaar" where a category has no method.
const OwnerBears = styled.div`
  width: ${METHOD_SELECT_WIDTH}px;
  max-width: 100%;
`;

const Chevron = styled.button<{ open: boolean }>`
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.getColor("gray", 600)};
  transform: rotate(${({ open }) => (open ? 180 : 0)}deg);
  transition: transform 0.15s ease;
`;

// Warnings under a category card's head; collapses when there is none.
const CardNotes = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  &:empty {
    display: none;
  }
`;

const Body = styled.div`
  margin-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  padding-top: 16px;
`;

// One option row in the verdeelsleutel dropdown: the key name on the left, its
// info "i" on the right.
const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  cursor: help;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

// Info popover next to a chosen verdeelsleutel: shows how that key splits the
// cost over the group's panden (each pand's share as a %), so the user can see
// what the selected sleutel actually does without leaving the step. Costs of
// one category can split over different panden (a pand archived before one of
// them began takes no part in it): then each split shows under the period of
// its costs.
const KeyInfo: React.FC<{
  keyDef?: ApportionmentKey;
  units: { id: string; code: string; address?: string; archivedOn?: string }[];
  // The own period of each of the category's costs.
  costPeriods: { from: string; to: string }[];
}> = ({ keyDef, units, costPeriods }) => {
  if (!keyDef) return null;
  const splits = keySplitsForCosts({ keyDef, units, periods: costPeriods });
  const content = (
    <div style={{ minWidth: 200 }}>
      <div style={{ fontWeight: FontWeights.SemiBold, marginBottom: 6 }}>
        {getLocalizedText("settlement.distribution.key_split_title", {
          name: keyDisplayName(keyDef.name),
        })}
      </div>
      {splits.map(split => {
        // A key without weight for any of these panden splits equally.
        const coversNone =
          !keyDef.isEqual &&
          split.units.every(u => !(Number(keyDef.weights[u.id]) > 0));
        return (
          <div key={`${split.from ?? ""}-${split.to ?? ""}`}>
            {splits.length > 1 && split.from && split.to && (
              <div style={{ marginTop: 6 }}>
                {fmtDate(split.from)} → {fmtDate(split.to)}
              </div>
            )}
            {coversNone || split.total <= 0 ? (
              <div>
                {getLocalizedText("settlement.distribution.equal_over_all")}
              </div>
            ) : (
              split.units.map(u => (
                <div
                  key={u.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                  }}
                >
                  <span>{u.address ?? u.code}</span>
                  <span>
                    {Math.round((split.weightOf(u.id) / split.total) * 100)}%
                  </span>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
  return (
    <Tooltip tooltipContent={content}>
      <InfoIcon>
        <Icon source="informationCircle" size="small" />
      </InfoIcon>
    </Tooltip>
  );
};

export const Step2Distribution: React.FC<
  StepContext & { result?: ComputeResult }
> = ctx => {
  const { draft, saveDraft, invoices, contracts, keys, units, ownerNames } =
    ctx;
  // A departure settlement charges its one tenant and nothing to the owner:
  // the tenant's amount stands in for the readings (see
  // departureMethodsForCategory) and the owner's part does not matter here.
  const isDeparture = !!ctx.departureContractId;
  const severalOwners = !isDeparture && Object.keys(ownerNames).length > 1;
  const [editing, setEditing] = useState<{
    category: Category;
    method: DistributionMethod;
  } | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const selected = invoices.filter(
    i => draft.selectedInvoiceIds[i.id] && settlementEligible(i),
  );
  const common = selected.filter(i => i.scope === "common");
  const departedPaidKeys = (category: Category) =>
    departedPaidOccupantKeys({
      departures: ctx.departures ?? [],
      invoices: common.filter(i => i.category === category),
    });
  const priv = selected.filter(i => i.scope === "private");
  const commonCategories = Array.from(new Set(common.map(i => i.category)));

  // Seed a sensible default per common category the first time this step is
  // reached: the group's own key when it has exactly one (that is the key the
  // manager set up for this), else the "Gelijke verdeling" key (or the first
  // available one), with the category's default method. Without this a
  // category the user never opened would have no key in the draft, and the
  // engine would push the whole cost to the owner instead of spreading it over
  // the tenants. A departure settlement stores the tenant's amount instead of
  // the consumption, so its confirmed view and statement read that method.
  const ownKeys = keys.filter(k => !k.isSystem);
  const defaultKey =
    ownKeys.length === 1
      ? ownKeys[0]
      : keys.find(k => k.isSystem && k.isEqual) ?? keys[0];
  React.useEffect(() => {
    const methodPatch = { ...draft.distributionMethod };
    const keyPatch = { ...draft.selectedKeyId };
    let changed = false;
    for (const cat of commonCategories) {
      const method = resolveDistributionMethod(cat, methodPatch, {
        isDeparture,
      });
      if (methodPatch[cat] !== method) {
        methodPatch[cat] = method;
        changed = true;
      }
      // Every method gets the key, not only "key": consumption uses it for
      // the excess "by key" and the owner's fixed share follows it too.
      if (keyPatch[cat] == null && defaultKey) {
        keyPatch[cat] = defaultKey.id;
        changed = true;
      }
    }
    if (changed) {
      saveDraft({ distributionMethod: methodPatch, selectedKeyId: keyPatch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonCategories.join(","), keys.length]);

  // The stretch the category's costs cover together: who lived where in it
  // decides the lines of the consumption / metering-company editor.
  const categoryPeriod = (cat: Category) => {
    const own = common.filter(i => i.category === cat);
    if (own.length === 0) return draft.period;
    return {
      from: own.reduce(
        (min, i) => (i.periodFrom < min ? i.periodFrom : min),
        own[0].periodFrom,
      ),
      to: own.reduce(
        (max, i) => (i.periodTo > max ? i.periodTo : max),
        own[0].periodTo,
      ),
    };
  };
  // The units a category's costs are split over: a unit archived before they
  // began takes no part (see ComputeInput.unitArchivedOn).
  const unitsFor = (cat: Category) => {
    const { from } = categoryPeriod(cat);
    return units.filter(u => existedSince({ archivedOn: u.archivedOn, from }));
  };

  return (
    <div>
      <StepHeader>
        {getLocalizedText("settlement.distribution.step_title")}
      </StepHeader>
      {/* One intro (Lewis, 2026-10-17): with units' own costs chosen, how
          they go to their tenants follows in the same block, not in a
          blue banner of its own under it. */}
      <div style={{ marginBottom: 16 }}>
        <TextStyle variation="subdued">
          {[
            isDeparture
              ? getLocalizedText("settlement.departure.distribution_intro", {
                  tenant: contracts[0]?.tenantName ?? "",
                })
              : getLocalizedText("settlement.distribution.intro"),
            ...(priv.length > 0
              ? [getLocalizedText("settlement.distribution.private_hint")]
              : []),
          ].join(" ")}
        </TextStyle>
      </div>
      <ContractOverlapWarning {...ctx} />
      {commonCategories.length === 0 && (
        <div style={{ padding: "16px 0" }}>
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.distribution.no_common_costs")}
          </TextStyle>
        </div>
      )}

      {/* No gap of our own: Card already brings the standard Rentio spacing
          via `Card + Card`, and together they left a double gap. */}
      <div>
        {commonCategories.map(cat => {
          const allowed = isDeparture
            ? departureMethodsForCategory(cat)
            : methodsForCategory(cat);
          const method = resolveDistributionMethod(
            cat,
            draft.distributionMethod,
            { isDeparture },
          );
          const catInvoices = common.filter(i => i.category === cat);
          const keyId = draft.selectedKeyId[cat] ?? keys[0]?.id;
          const keyDef = keys.find(k => k.id === keyId);
          const isOpen = !!open[cat];
          const catUnits = unitsFor(cat);
          const catCostPeriods = catInvoices.map(i => ({
            from: i.periodFrom,
            to: i.periodTo,
          }));
          // The same coverage warning as the cost popup: a key that misses
          // panden gives them € 0, one that covers none splits equally.
          const keySplitsTenants =
            method === "key" ||
            (method === "consumption" &&
              (draft.excessMode[cat] ?? "landlord") === "key");
          const coverage =
            keyDef && keySplitsTenants
              ? keyCoverage({
                  unitIds: catUnits.map(u => u.id),
                  weights: keyDef.weights,
                  isEqual: keyDef.isEqual,
                })
              : undefined;
          const consumption = draft.consumption[cat] ?? {};
          const problem = categoryProblem(ctx, cat);
          // Another key than a departure of this category used (decision 2).
          const keyDifferences =
            method === "key"
              ? departureKeyDifferences({
                  category: cat,
                  keyId,
                  departures: ctx.departures ?? [],
                  alreadySettled: ctx.result?.alreadySettled ?? [],
                })
              : [];
          // A departed tenant whose departure already charged them for this
          // category (Y12): left empty, that amount comes off the cost first
          // (the engine's departuresWithEmptyReading); said apart, with the
          // amount, instead of "pays € 0,00".
          const departedPaid =
            method === "consumption" && isPerOccupant(consumption)
              ? (ctx.departures ?? []).flatMap(departure => {
                  const reading =
                    consumption[occupantKey.contract(departure.contractId)] ??
                    0;
                  if (reading > 0) return [];
                  const paidCents = catInvoices.reduce(
                    (sum, i) =>
                      sum + toCents(departure.costByInvoice[i.id] ?? 0),
                    0,
                  );
                  return paidCents === 0
                    ? []
                    : [{ departure, amount: paidCents / 100 }];
                })
              : [];
          // The departing tenant's own amount, once filled in (M35).
          const tenantAmount = departureTenantAmountNote(ctx, cat);
          // A departed lease's empty reading stands for what its departure
          // charged; any other lease, of the same name too, still needs one
          // (M14).
          const missingReadings =
            method === "consumption" && isPerOccupant(consumption)
              ? tenantsWithoutConsumption({
                  units: catUnits,
                  contracts,
                  period: categoryPeriod(cat),
                  consumption,
                  excusedKeys: departedPaidKeys(cat),
                })
              : [];

          return (
            <Card key={cat}>
              <CardHead>
                <CardTitle>
                  <IconCircle>
                    <Icon source={categoryIcon(cat)} color="primary" />
                  </IconCircle>
                  <HeadTitle>
                    {CATEGORY_LABELS_NL[cat]} ({catInvoices.length})
                  </HeadTitle>
                </CardTitle>
                <HeadControls>
                  {allowed.length === 0 ? (
                    // Categories without distribution methods (owner-only, e.g.
                    // loan repayments) have nothing to choose — the engine
                    // sends them to the owner; an empty Select only confuses.
                    <OwnerBears>
                      <TextStyle variation="subdued">
                        {getLocalizedText(
                          "settlement.distribution.owner_bears",
                        )}
                      </TextStyle>
                    </OwnerBears>
                  ) : (
                    <>
                      <SelectControl>
                        <Select
                          id={`method-${cat}`}
                          label="_"
                          labelHidden
                          value={method}
                          options={allowed.map(m => ({
                            value: m,
                            label: methodLabel(m, cat, { isDeparture }),
                          }))}
                          onChange={(opt: any) =>
                            saveDraft({
                              distributionMethod: {
                                ...draft.distributionMethod,
                                [cat]: opt.value as DistributionMethod,
                              },
                            })
                          }
                        />
                      </SelectControl>
                      {method === "key" ? (
                        <SelectControl>
                          <Select
                            id={`key-${cat}`}
                            label="_"
                            labelHidden
                            value={keyId}
                            options={keys.map(k => ({
                              value: k.id,
                              label: keyDisplayName(k.name),
                              keyDef: k,
                            }))}
                            optionRender={(props: any) => (
                              <OptionRow>
                                <span>{props.data.label}</span>
                                <KeyInfo
                                  keyDef={props.data.keyDef}
                                  units={catUnits}
                                  costPeriods={catCostPeriods}
                                />
                              </OptionRow>
                            )}
                            onChange={(opt: any) =>
                              saveDraft({
                                selectedKeyId: {
                                  ...draft.selectedKeyId,
                                  [cat]: opt.value,
                                },
                              })
                            }
                          />
                        </SelectControl>
                      ) : (
                        <Button
                          appearance="outline"
                          onClick={() => setEditing({ category: cat, method })}
                        >
                          {getLocalizedText("costs.action.edit")}
                        </Button>
                      )}
                    </>
                  )}
                  <Chevron
                    open={isOpen}
                    onClick={() => setOpen(p => ({ ...p, [cat]: !p[cat] }))}
                    aria-label={getLocalizedText(
                      isOpen ? "system.collapse" : "system.expand",
                    )}
                  >
                    <Icon source="chevronDown" />
                  </Chevron>
                </HeadControls>
              </CardHead>

              <CardNotes>
                {problem && (
                  <Banner variation="warning" hasDismiss={false}>
                    {problem.reason}{" "}
                    <Button
                      appearance="link"
                      onClick={() =>
                        setEditing({ category: cat, method: problem.method })
                      }
                    >
                      {getLocalizedText(problem.actionKey)}
                    </Button>
                  </Banner>
                )}
                {coverage && (
                  <KeyCoverageWarning
                    coverage={coverage}
                    units={catUnits.map(u => ({
                      id: u.id,
                      label: u.address ?? u.code,
                    }))}
                    keyName={keyDef?.name}
                  />
                )}
                {keyDifferences.map(({ departure, amount }) => (
                  <Banner
                    key={departure.settlementId}
                    variation="warning"
                    hasDismiss={false}
                  >
                    {getLocalizedText(
                      amount > 0
                        ? "settlement.distribution.departure_key_more"
                        : "settlement.distribution.departure_key_less",
                      {
                        tenant: departure.tenantName,
                        category: CATEGORY_LABELS_NL[cat],
                        departureKey: keyDisplayName(
                          keys.find(
                            k => k.id === departure.keyIdByCategory?.[cat],
                          )?.name ?? "—",
                        ),
                        key: keyDisplayName(keyDef?.name ?? "—"),
                        amount: fmt(Math.abs(amount)),
                      },
                    )}
                  </Banner>
                ))}
                {departedPaid.map(({ departure, amount }) => (
                  <Banner
                    key={`paid-${departure.settlementId}`}
                    variation="info"
                    hasDismiss={false}
                  >
                    {getLocalizedText(
                      "settlement.editor.consumption_departed_paid",
                      {
                        tenant: departure.tenantName,
                        amount: fmt(amount),
                        category: CATEGORY_LABELS_NL[cat],
                      },
                    )}
                  </Banner>
                ))}
                {tenantAmount && (
                  <Banner variation="info" hasDismiss={false}>
                    {getLocalizedText(
                      "settlement.departure.tenant_amount_rest",
                      {
                        amount: fmt(tenantAmount.amount),
                        tenant: tenantAmount.tenant,
                        rest: fmt(tenantAmount.rest),
                      },
                    )}
                  </Banner>
                )}
                {missingReadings.length > 0 && (
                  <Banner variation="warning" hasDismiss={false}>
                    {getLocalizedText(
                      missingReadings.length === 1
                        ? "settlement.editor.consumption_missing_one"
                        : "settlement.editor.consumption_missing_many",
                      {
                        names: missingReadings.join(", "),
                        amount: fmt(0),
                        category: CATEGORY_LABELS_NL[cat],
                      },
                    )}
                  </Banner>
                )}
              </CardNotes>

              {isOpen && (
                <Body>
                  <InvoiceLines invoices={catInvoices} />
                </Body>
              )}
            </Card>
          );
        })}
      </div>

      {editing && (
        <DistributionEditorModal
          category={editing.category}
          method={editing.method}
          total={common
            .filter(i => i.category === editing.category)
            .reduce((s, i) => s + settlementAmount(i), 0)}
          period={categoryPeriod(editing.category)}
          contracts={contracts}
          units={unitsFor(editing.category)}
          keys={keys}
          draft={draft}
          // The owner's part of a consumption or metering-company category
          // is split over the units by a key, which only decides what each
          // owner pays once there are several (Lewis, 2026-10-17).
          ownerPartMatters={severalOwners}
          ownerCount={Object.keys(ownerNames).length}
          ownerKeyByUnit={ctx.ownerKeyByUnit}
          ownerNames={ownerNames}
          isDeparture={isDeparture}
          excusedKeys={departedPaidKeys(editing.category)}
          onChange={patch => saveDraft(patch)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

// The occupant lines of the departed leases whose departure already charged
// one of these costs (Y12): their empty reading stands for that amount, so
// it is no missing reading. By lease, never by tenant name (M14).
export const departedPaidOccupantKeys = (params: {
  departures: Pick<ISettledDeparture, "contractId" | "costByInvoice">[];
  invoices: Pick<Invoice, "id">[];
}): string[] =>
  params.departures
    .filter(departure =>
      params.invoices.some(
        invoice => toCents(departure.costByInvoice[invoice.id] ?? 0) !== 0,
      ),
    )
    .map(departure => occupantKey.contract(departure.contractId));

const InvoiceLines: React.FC<{ invoices: Invoice[] }> = ({ invoices }) => {
  return (
    <DataTable
      columnContentTypes={["text", "text", "text", "numeric"]}
      headings={[
        getLocalizedText("costs.field.supplier"),
        getLocalizedText("costs.field.date"),
        getLocalizedText("costs.field.description"),
        getLocalizedText("costs.field.amount_excl_vat"),
      ]}
      rows={invoices.map(i => ({
        id: i.id,
        content: [
          i.supplier || "—",
          fmtDate(i.date),
          i.description || "—",
          <MoneyWithVat amount={i.amountExclVat} vat={btwOf(i)} />,
        ],
      }))}
    />
  );
};

// What a departure's card says once the tenant's amount from the metering
// company is filled in (M35): that amount and the rest that stays open, as
// the editor says it, so Bea can check it without opening the editor.
// Undefined while it is missing or too high (categoryProblem says so).
export const departureTenantAmountNote = (
  ctx: Pick<
    StepContext,
    "draft" | "invoices" | "contracts" | "departureContractId"
  >,
  category: Category,
): { amount: number; rest: number; tenant: string } | undefined => {
  const { draft, departureContractId } = ctx;
  if (!departureContractId) return undefined;
  const method = resolveDistributionMethod(category, draft.distributionMethod, {
    isDeparture: true,
  });
  if (method !== "company") return undefined;
  const totalCents = ctx.invoices
    .filter(
      i =>
        draft.selectedInvoiceIds[i.id] &&
        settlementEligible(i) &&
        i.scope === "common" &&
        i.category === category,
    )
    .reduce((sum, i) => sum + toCents(settlementAmount(i)), 0);
  const amountCents = toCents(
    draft.companyAmounts[category]?.[
      occupantKey.contract(departureContractId)
    ] ?? 0,
  );
  if (!(amountCents > 0) || amountCents > totalCents) return undefined;
  return {
    amount: fromCents(amountCents),
    rest: fromCents(totalCents - amountCents),
    tenant:
      ctx.contracts.find(c => c.id === departureContractId)?.tenantName ?? "",
  };
};

// A departure settlement's amount for its tenant from the metering company:
// it has to be there, and cannot be more than the category's costs (the rest
// stays open for the settlement of the group).
const departureTenantAmountProblem = (params: {
  ctx: StepContext;
  category: Category;
  departureContractId: string;
  total: number;
}): string | undefined => {
  const { ctx, category, departureContractId, total } = params;
  const amount =
    ctx.draft.companyAmounts[category]?.[
      occupantKey.contract(departureContractId)
    ] ?? 0;
  const texts = {
    category: CATEGORY_LABELS_NL[category],
    tenant:
      ctx.contracts.find(c => c.id === departureContractId)?.tenantName ?? "",
  };
  if (!(toCents(amount) > 0)) {
    return getLocalizedText(
      "settlement.departure.validation.tenant_amount_missing",
      texts,
    );
  }
  if (toCents(amount) > toCents(total)) {
    return getLocalizedText(
      "settlement.departure.validation.tenant_amount_exceeds",
      { ...texts, total: fmt(total) },
    );
  }
  return undefined;
};

// Why one category of building costs cannot be settled yet, and what the
// broker fills in to fix it (Lewis, 2026-10-17: said at the category, with
// a button that opens its editor, and above the step's buttons).
export interface IDistributionProblem {
  category: Category;
  reason: string;
  // The editor's method and the button that opens it.
  method: DistributionMethod;
  actionKey: string;
}

const categoryProblem = (
  ctx: StepContext,
  category: Category,
): IDistributionProblem | undefined => {
  const selected = ctx.invoices.filter(
    i =>
      ctx.draft.selectedInvoiceIds[i.id] &&
      settlementEligible(i) &&
      i.scope === "common" &&
      i.category === category,
  );
  if (selected.length === 0) return undefined;
  const { departureContractId } = ctx;
  const method = resolveDistributionMethod(
    category,
    ctx.draft.distributionMethod,
    { isDeparture: !!departureContractId },
  );
  const total = selected.reduce((s, i) => s + settlementAmount(i), 0);
  const problem = (reason: string, actionKey: string) => ({
    category,
    reason,
    method,
    actionKey,
  });
  if (method === "company" && departureContractId) {
    const reason = departureTenantAmountProblem({
      ctx,
      category,
      departureContractId,
      total,
    });
    return reason
      ? problem(reason, "settlement.distribution.fill_tenant_amount")
      : undefined;
  }
  if (method === "company") {
    // To the cent, like the editor shows it: a float sum let a one-cent gap
    // through, which the engine then quietly shifted onto the owner or the
    // tenants.
    const isMatched = addsUpTo({
      parts: Object.values(ctx.draft.companyAmounts[category] ?? {}),
      total,
    });
    return isMatched
      ? undefined
      : problem(
          getLocalizedText(
            "settlement.distribution.validation.company_mismatch",
            { category: CATEGORY_LABELS_NL[category] },
          ),
          "settlement.distribution.fill_amounts",
        );
  }
  if (method !== "consumption") return undefined;
  // Consumption without a total has no denominator to divide by: the whole
  // cost would land on the owner instead of the tenants. Block that here so
  // the user fills in the meter readings.
  const texts = { category: CATEGORY_LABELS_NL[category] };
  const buildingTotal = ctx.draft.buildingTotal[category] ?? 0;
  if (buildingTotal <= 0) {
    return problem(
      getLocalizedText(
        "settlement.distribution.validation.total_consumption_missing",
        texts,
      ),
      "settlement.distribution.fill_consumption",
    );
  }
  const consumed = Object.values(ctx.draft.consumption[category] ?? {}).reduce(
    (a, b) => a + b,
    0,
  );
  if (consumed <= 0) {
    return problem(
      getLocalizedText(
        "settlement.distribution.validation.unit_consumption_missing",
        texts,
      ),
      "settlement.distribution.fill_consumption",
    );
  }
  // The other direction charges MORE than the cost: a share is
  // consumption/buildingTotal, so readings above the total bill the tenants
  // over 100% while the owner carries nothing.
  if (consumed > buildingTotal + 0.005) {
    return problem(
      getLocalizedText(
        "settlement.distribution.validation.unit_consumption_exceeds",
        texts,
      ),
      "settlement.distribution.fill_consumption",
    );
  }
  return undefined;
};

export const Step2Validate = (
  ctx: StepContext,
): { ok: boolean; reason?: string } => {
  const categories = Array.from(
    new Set(
      ctx.invoices
        .filter(
          i =>
            ctx.draft.selectedInvoiceIds[i.id] &&
            settlementEligible(i) &&
            i.scope === "common",
        )
        .map(i => i.category),
    ),
  );
  for (const category of categories) {
    const problem = categoryProblem(ctx, category);
    if (problem) return { ok: false, reason: problem.reason };
  }
  return { ok: true };
};
